import React from 'react';
import { Retry } from '@components/common';
import { DefaultLayer as Loading } from '@components/common/Retry/Elements';
import DrillDownTreemap from '../DrillDownTreemap';
import { WidgetContainer } from '../../styled';
import { useInteraction, useConflictFilters, type Level } from '../DrillDownTreemap/hooks';
import { useDimensionGroupBy } from '../hooks/useTreemapHooks';
import { useExternalState } from '../hooks/useExternalState';
import { NETWORK_REQUEST_RESPONSE_CODE_DIMENSION } from '../constants';
import { getFilters } from '../helpers';
import { getCodeColorMap } from './utils';
import NetworkContext from '../context';
import type { NavItem } from '../DrillDownTreemap/Breadcrumb';
import { useAvailable } from '../hooks/useAvailable';

const drillDownLevels: Level = {
  key: 'network_request_response_code', // all distribution
  next: {
    key: 'network_request_url_host', // 1-2s
    next: {
      key: 'network_request_url_path', // 1-2s, host
    },
  },
};

const rootNavItem: NavItem = {
  label: 'Network Request Count',
  key: drillDownLevels.key,
};

function createLevel(level: Level) {
  return {
    type: level.key === drillDownLevels.key ? 'root' : 'children',
    labelField: 'name',
    valueField: 'AverageRequestCount',
    drillDownField: level.key,
    next: level.next,
    partitionBy: NETWORK_REQUEST_RESPONSE_CODE_DIMENSION,
  } as const;
}

function CodeTreeMap(props?: { withTotals?: boolean }) {
  const { withTotals = false } = props ?? {};
  const { enableSSF, enableTimeSlice, onSelect, onResetCallback, formatter } =
    React.useContext(NetworkContext);
  const { interval } = useAvailable(enableSSF, enableTimeSlice);
  const { reset, enableLoading, keepPreviousData } = useExternalState(enableSSF);

  const { navItems, levelInfo, onNavigate, onDrillDown, onReset } = useInteraction({
    rootNavItem,
    drillDownLevels,
    createLevel,
    onResetCallback,
    onSelect,
  });

  // reset to root when real time mode is on
  React.useEffect(() => {
    if (reset) {
      onReset();
    }
  }, [onReset, reset]);

  const allFilters = getFilters(navItems);

  const hasConflict = useConflictFilters(allFilters.filters, onReset);

  const dataSelector = React.useCallback(
    (data: Record<string, any>[] | undefined) => {
      const code = navItems.find((i) => i.filter?.key === NETWORK_REQUEST_RESPONSE_CODE_DIMENSION)
        ?.filter?.value;
      return data
        ?.map((i) => ({ network_request_response_code: code, ...i }))
        .filter((i) => i.network_request_response_code);
    },
    [navItems],
  );

  const { data, total, ...status } = useDimensionGroupBy({
    dimension: levelInfo.drillDownField,
    metrics: ['AverageRequestCount'],
    sortByMetric: 'AverageRequestCount',
    enabled:
      !reset && !hasConflict && levelInfo.drillDownField === navItems[navItems.length - 1].key,
    interval,
    keepPreviousData,
    enableSSF,
    select: dataSelector,
    withTotals,
    ...allFilters,
  });

  const validData = React.useMemo(() => {
    return data?.filter((item) => !['UNKNOWN'].includes(item.network_request_response_code));
  }, [data]);

  const top5Data = React.useMemo(() => validData?.slice(0, 5), [validData]);

  const colorMap = React.useMemo(() => {
    const codes = new Set<string>(validData?.map((i) => i.network_request_response_code).flat());
    return getCodeColorMap(Array.from(codes));
  }, [validData]);

  return (
    <WidgetContainer $isEmpty={status.isEmpty}>
      <Retry {...status} isEmpty={false} progress>
        {enableLoading && status.isLoading && <Loading progress duration={1} />}
        <DrillDownTreemap
          id="code"
          title="Network Request Count"
          total={withTotals ? total : undefined}
          formatter={formatter}
          drillDown={!!levelInfo.next}
          data={levelInfo.type === 'root' ? top5Data : validData}
          navItems={navItems}
          colorMap={colorMap}
          labelField={levelInfo.labelField}
          valueField={levelInfo.valueField}
          partitionBy={levelInfo.partitionBy}
          onDrillDown={onDrillDown}
          onNavigate={onNavigate}
        />
      </Retry>
    </WidgetContainer>
  );
}

export default CodeTreeMap;
